import React from 'react';
import { useNavigate } from 'react-router-dom';
import { date, mixed, number, object, string } from 'yup';

import Cabecalho from 'Component/Admin/Cabecalho';
import { useAlerta, useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Form, { Arquivo, Botao, Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import { Erro } from 'Interface/Requisicao';
import Usuario from 'Interface/Usuarios';
import { useAdicionarUsuarioMutation } from 'Service';
import {
  ESTADOS,
  IMAGENS,
  MASCARA_CPF,
  MASCARA_TELEFONE,
  PERMISSOES,
} from 'View/constants';
import { errosParaLista } from 'View/functions';

const regras = object({
  nome: string().required('Obrigatório'),
  email: string().email('Inválido').required('Obrigatório'),
  senha: string().min(6, 'Mínimo de 6 caracteres').required('Obrigatório'),
  cpf: string().required('Obrigatório'),
  rg: string().nullable().optional(),
  orgao_emissor: string().nullable().optional(),
  estado_orgao_emissor: string().nullable().optional(),
  telefone1: string().required('Obrigatório'),
  telefone2: string().nullable().optional(),
  nascimento: date().nullable().optional(),
  cep: string().nullable().optional(),
  estado: string().nullable().optional(),
  cidade: string().nullable().optional(),
  bairro: string().nullable().optional(),
  endereco: string().nullable().optional(),
  numero: number().nullable().integer().positive('Inválido').optional(),
  complemento: string().nullable().optional(),
  imagem: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return IMAGENS.includes(valor.type);
      }
      return true;
    })
    .optional(),
  permissao: string()
    .is(['super', 'admin', 'comum', 'conteudista'])
    .required('Obrigatório'),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const alerta = useAlerta();
  const [adicionarUsuario] = useAdicionarUsuarioMutation();

  const enviar = async (dados: Usuario): Promise<void> => {
    try {
      await adicionarUsuario(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao('/admin/usuarios');
    } catch (error) {
      alerta({
        titulo: 'Erro ao editar',
        conteudo: errosParaLista(error as Erro),
        tema: 'danger',
      });
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <Cabecalho
        titulo="Adicionando usuário"
        descricao="Preencha o formulário e clique em enviar para adicionar o usuário"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row">
          <div className="col-md-9">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Campo name="nome" rotulo="Nome" />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="email"
                    rotulo="E-mail"
                    prefixar={<Icone nome="envelope-fill" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="senha"
                    rotulo="Senha"
                    type="password"
                    prefixar={<Icone nome="lock-fill" />}
                  />
                </div>
                <div className="col-md-3">
                  <Campo name="cpf" rotulo="CPF" mascara={MASCARA_CPF} />
                </div>
                <div className="col-md-3">
                  <Campo name="rg" rotulo="RG" />
                </div>
                <div className="col-md-3">
                  <Campo name="orgao_emissor" rotulo="Orgão emissor" />
                </div>
                <div className="col-md-3">
                  <Campo
                    name="estado_orgao_emissor"
                    rotulo="Estado do orgão emissor"
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="telefone1"
                    rotulo="Telefone 1"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="telefone2"
                    rotulo="Telefone 2"
                    mascara={MASCARA_TELEFONE}
                    prefixar={<Icone nome="telephone-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="nascimento"
                    type="date"
                    rotulo="Nascimento"
                    prefixar={<Icone nome="calendar-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="cep"
                    rotulo="CEP"
                    prefixar={<Icone nome="geo-fill" />}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="estado"
                    rotulo="Estado onde reside"
                    opcoes={ESTADOS}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="cidade" rotulo="Cidade onde reside" />
                </div>
                <div className="col-md-4">
                  <Campo name="bairro" rotulo="Bairro onde reside" />
                </div>
                <div className="col-md-6">
                  <Campo name="endereco" rotulo="Endereço de onde reside" />
                </div>
                <div className="col-md-2">
                  <Campo name="numero" type="number" rotulo="Número" />
                </div>
                <div className="col-md-12">
                  <Campo name="complemento" rotulo="Complemento" semMargem />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="bg-white shadow-sm rounded p-4">
              <Arquivo name="imagem" />
              <Selecao
                name="permissao"
                rotulo="Permissão"
                prefixar={<Icone nome="lock-fill" />}
                opcoes={PERMISSOES}
              />

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink tema="light" to="/admin/usuarios" className="ms-2">
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default Adicionar;
